import { useEffect, useRef, useState, forwardRef, useImperativeHandle } from "react";
import { Loader2 } from "lucide-react";
import { SQUARE_APP_ID, SQUARE_LOCATION_ID, SQUARE_ENV } from "@/lib/api";
import { logError } from "@/lib/logger";

const SDK_URL =
  SQUARE_ENV === "production"
    ? "https://web.squarecdn.com/v1/square.js"
    : "https://sandbox.web.squarecdn.com/v1/square.js";

function loadSdk() {
  return new Promise((resolve, reject) => {
    if (window.Square) return resolve(window.Square);
    const existing = document.getElementById("square-sdk");
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Square));
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "square-sdk";
    s.src = SDK_URL;
    s.async = true;
    s.onload = () => resolve(window.Square);
    s.onerror = reject;
    document.body.appendChild(s);
  });
}

// Exposes tokenize() to the parent via ref. Returns a single-use payment token.
export const SquareCard = forwardRef((props, ref) => {
  const containerRef = useRef(null);
  const cardRef = useRef(null);
  const [status, setStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    let mounted = true;
    (async () => {
      let localCard = null;
      try {
        const Square = await loadSdk();
        const payments = Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        localCard = await payments.card({
          style: {
            input: {
              color: "#0F4A23",
              fontSize: "15px",
            },
            "input::placeholder": { color: "#9A9790" },
            ".input-container": {
              borderColor: "#E2DFD8",
              borderRadius: "12px",
            },
            ".input-container.is-focus": { borderColor: "#C8202B" },
            ".input-container.is-error": { borderColor: "#dc2626" },
            ".message-text": { color: "#8A8880" },
            ".message-icon": { color: "#8A8880" },
            ".message-text.is-error": { color: "#dc2626" },
            ".message-icon.is-error": { color: "#dc2626" },
            "input.is-error": { color: "#dc2626" },
          },
        });
        if (!mounted) { try { localCard.destroy(); } catch (_) {} return; }
        await localCard.attach(containerRef.current);
        if (!mounted) { try { localCard.destroy(); } catch (_) {} return; }
        cardRef.current = localCard;
        setStatus("ready");
      } catch (e) {
        logError("Square SDK failed to initialise", e);
        if (mounted) setStatus("error");
      }
    })();
    return () => {
      mounted = false;
      if (cardRef.current) {
        try { cardRef.current.destroy(); } catch (_) {}
      }
    };
  }, []);

  useImperativeHandle(ref, () => ({
    async tokenize() {
      if (!cardRef.current) throw new Error("Card not ready");
      const result = await cardRef.current.tokenize();
      if (result.status !== "OK") {
        throw new Error(result.errors?.[0]?.message || "Card details are invalid");
      }
      return { token: result.token, last4: result.details?.card?.last4 || "" };
    },
    ready: status === "ready",
  }));

  return (
    <div data-testid="square-card">
      {status === "loading" && (
        <div className="flex items-center gap-2 py-6 text-muted-foreground">
          <Loader2 className="h-5 w-5 animate-spin" /> Loading secure card form…
        </div>
      )}
      {status === "error" && (
        <p className="rounded-lg bg-destructive/10 px-4 py-3 text-sm text-destructive">
          Could not load the Square card form. Please refresh and try again.
        </p>
      )}
      <div ref={containerRef} className={status === "ready" ? "" : "hidden"} />
      {status === "ready" && SQUARE_ENV !== "production" && (
        <p className="mt-2 text-[11px] text-muted-foreground">Test card: 4111 1111 1111 1111 · any future date · any CVV · any postal</p>
      )}
    </div>
  );
});

SquareCard.displayName = "SquareCard";
